import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MDBox from '../MDBox';
import MDTypography from '../MDTypography';

// Map risk category to detection threshold
const getThresholdFromRisk = (riskCategory) => {
  const thresholdMap = {
    'Low': 0.70,
    'Moderate': 0.60,
    'High': 0.50,
    'Extreme': 0.25,
  };
  return thresholdMap[riskCategory] || 0.70;
};

// Get explanation based on risk category
const getThresholdExplanation = (riskCategory) => {
  const explanations = {
    'Low': 'In low-risk conditions, the system maintains a conservative threshold to minimize false alarms.',
    'Moderate': 'In moderate-risk conditions, the system balances sensitivity with false alarm reduction.',
    'High': 'In high-risk conditions, the system lowers its detection threshold to increase sensitivity and detect fires earlier.',
    'Extreme': 'In extreme-risk conditions, the system maximizes sensitivity with a very low threshold for earliest possible fire detection.',
  };
  return explanations[riskCategory] || 'Detection threshold adjusts based on current fire danger level.';
};

function AdaptiveThresholdSection({ riskCategory, fwiZ }) {
  const threshold = getThresholdFromRisk(riskCategory);
  const explanation = getThresholdExplanation(riskCategory);

  // Example signal for demo
  const exampleSignal = 0.55;

  return (
    <MDBox mt={6} mb={3}>
      {/* Section Title */}
      <MDTypography variant="h4" fontWeight="medium" color="info" textTransform="uppercase" textAlign="left" mb={1}>
        Adaptive Detection Threshold
      </MDTypography>
      
      {/* Introduction */}
      <MDTypography variant="body1" color="text" textAlign="left" mb={3}>
        Wildfire danger estimates are not only used to assess risk, but also to adjust how sensitive fire detection systems should be. 
        In higher-risk conditions, detection systems can lower their threshold to identify fires earlier, while in lower-risk conditions, 
        they remain more conservative to reduce false alarms.
      </MDTypography>

      <Grid container spacing={3}>
        {/* Current Detection Policy Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" color="dark" mb={2}>
                Current Detection Policy
              </MDTypography>
              
              <MDBox mb={2}>
                <MDTypography variant="button" color="text" fontWeight="medium">
                  Fire Danger Level:
                </MDTypography>
                <MDTypography 
                  variant="h5" 
                  fontWeight="bold" 
                  mt={0.5}
                  sx={{
                    color: 
                      riskCategory === 'Extreme' ? '#F44336' :
                      riskCategory === 'High' ? '#FF9800' :
                      riskCategory === 'Moderate' ? '#FFC107' : '#2196F3'
                  }}
                >
                  {riskCategory || 'N/A'}
                </MDTypography>
              </MDBox>

              <MDBox mb={2}>
                <MDTypography variant="button" color="text" fontWeight="medium">
                  Detection Threshold:
                </MDTypography>
                <MDTypography variant="h5" fontWeight="bold" mt={0.5}>
                  {threshold.toFixed(2)}
                </MDTypography>
              </MDBox>

              <MDBox 
                p={2} 
                sx={{ 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '8px',
                  borderLeft: '4px solid #1976d2'
                }}
              >
                <MDTypography variant="body2" color="text" fontStyle="italic">
                  {explanation}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Threshold Tradeoff Explanation */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" color="dark" mb={2}>
                Why Thresholds Matter
              </MDTypography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MDBox 
                    p={2} 
                    sx={{ 
                      backgroundColor: '#fff3e0', 
                      borderRadius: '8px',
                      border: '1px solid #ffb74d',
                      height: '100%'
                    }}
                  >
                    <MDTypography variant="button" fontWeight="bold" color="warning" mb={1}>
                      Lower Threshold
                    </MDTypography>
                    <MDBox component="ul" pl={2} mb={0}>
                      <li>
                        <MDTypography variant="caption" color="text">
                          Detects fires earlier
                        </MDTypography>
                      </li>
                      <li>
                        <MDTypography variant="caption" color="text">
                          More sensitive
                        </MDTypography>
                      </li>
                      <li>
                        <MDTypography variant="caption" color="text">
                          Higher chance of false alarms
                        </MDTypography>
                      </li>
                    </MDBox>
                  </MDBox>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MDBox 
                    p={2} 
                    sx={{ 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: '8px',
                      border: '1px solid #64b5f6',
                      height: '100%'
                    }}
                  >
                    <MDTypography variant="button" fontWeight="bold" color="info" mb={1}>
                      Higher Threshold
                    </MDTypography>
                    <MDBox component="ul" pl={2} mb={0}>
                      <li>
                        <MDTypography variant="caption" color="text">
                          More conservative
                        </MDTypography>
                      </li>
                      <li>
                        <MDTypography variant="caption" color="text">
                          Fewer false alarms
                        </MDTypography>
                      </li>
                      <li>
                        <MDTypography variant="caption" color="text">
                          May detect fires later
                        </MDTypography>
                      </li>
                    </MDBox>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>

        {/* Decision Demo */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" color="dark" mb={2}>
                Same Signal, Different Decisions
              </MDTypography>

              <MDTypography variant="body2" color="text" mb={2}>
                For the same satellite signal strength, different thresholds produce different detection outcomes:
              </MDTypography>

              <MDBox 
                mb={2} 
                p={2} 
                sx={{ 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '8px',
                  display: 'inline-block'
                }}
              >
                <MDTypography variant="button" fontWeight="medium" color="text">
                  Example Signal Strength: 
                </MDTypography>
                <MDTypography variant="h6" fontWeight="bold" color="dark" component="span" ml={1}>
                  {exampleSignal.toFixed(2)}
                </MDTypography>
              </MDBox>

              <TableContainer>
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="button" fontWeight="bold" color="text">
                          Threshold
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="button" fontWeight="bold" color="text">
                          Risk Level
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="button" fontWeight="bold" color="text">
                          Decision
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ backgroundColor: exampleSignal >= 0.70 ? '#e8f5e9' : '#ffebee' }}>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="body2" fontWeight="medium">
                          0.70
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="body2" color="info">
                          Low
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography 
                          variant="body2" 
                          fontWeight="bold"
                          sx={{ color: exampleSignal >= 0.70 ? '#4caf50' : '#f44336' }}
                        >
                          {exampleSignal >= 0.70 ? '✓ Fire Detected' : '✗ No Fire Detected'}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: exampleSignal >= 0.60 ? '#e8f5e9' : '#ffebee' }}>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="body2" fontWeight="medium">
                          0.60
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="body2" sx={{ color: '#FFC107' }}>
                          Moderate
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography 
                          variant="body2" 
                          fontWeight="bold"
                          sx={{ color: exampleSignal >= 0.60 ? '#4caf50' : '#f44336' }}
                        >
                          {exampleSignal >= 0.60 ? '✓ Fire Detected' : '✗ No Fire Detected'}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: exampleSignal >= 0.50 ? '#e8f5e9' : '#ffebee' }}>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="body2" fontWeight="medium">
                          0.50
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="body2" sx={{ color: '#FF9800' }}>
                          High
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography 
                          variant="body2" 
                          fontWeight="bold"
                          sx={{ color: exampleSignal >= 0.50 ? '#4caf50' : '#f44336' }}
                        >
                          {exampleSignal >= 0.50 ? '✓ Fire Detected' : '✗ No Fire Detected'}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: exampleSignal >= 0.25 ? '#e8f5e9' : '#ffebee' }}>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="body2" fontWeight="medium">
                          0.25
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography variant="body2" sx={{ color: '#F44336' }}>
                          Extreme
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <MDTypography 
                          variant="body2" 
                          fontWeight="bold"
                          sx={{ color: exampleSignal >= 0.25 ? '#4caf50' : '#f44336' }}
                        >
                          {exampleSignal >= 0.25 ? '✓ Fire Detected' : '✗ No Fire Detected'}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <MDBox 
                mt={3} 
                p={2} 
                sx={{ 
                  backgroundColor: '#e3f2fd', 
                  borderRadius: '8px',
                  borderLeft: '4px solid #2196f3'
                }}
              >
                <MDTypography variant="body2" color="text">
                  <strong>Key Insight:</strong> For the same satellite signal ({exampleSignal.toFixed(2)}), 
                  a lower threshold results in earlier detection. This is why threshold adjustment matters 
                  in high-risk conditions — it enables the system to respond faster when fires are more likely.
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Collapsible "How Are Thresholds Chosen?" Section */}
        <Grid item xs={12}>
          <Accordion 
            sx={{ 
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              '&:before': {
                display: 'none',
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
            >
              <MDTypography variant="h6" fontWeight="medium" color="dark">
                How Are Thresholds Chosen?
              </MDTypography>
            </AccordionSummary>
            <AccordionDetails>
              <MDBox>
                {/* Paragraph 1: Overview */}
                <MDTypography variant="body2" color="text" mb={2}>
                  Detection thresholds are not arbitrary — they are based on the level of fire danger. 
                  We use the standardized Fire Weather Index (FWI_z) to measure how extreme current 
                  conditions are compared to historical data.
                </MDTypography>

                {/* Paragraph 2: Process */}
                <MDTypography variant="body2" color="text" mb={2}>
                  FWI_z values are grouped into risk categories (Low, Moderate, High, Extreme). 
                  Each category is assigned a different detection threshold. This creates an adaptive 
                  system that adjusts sensitivity based on environmental conditions.
                </MDTypography>

                {/* Bullet Points */}
                <MDBox 
                  p={2} 
                  mb={2}
                  sx={{ 
                    backgroundColor: '#fff3e0', 
                    borderRadius: '8px',
                    borderLeft: '4px solid #ff9800'
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold" color="text" mb={1}>
                    Key Idea:
                  </MDTypography>
                  <MDBox component="ul" pl={2} mb={0}>
                    <li>
                      <MDTypography variant="body2" color="text">
                        <strong>Higher fire risk</strong> → lower threshold → earlier detection
                      </MDTypography>
                    </li>
                    <li>
                      <MDTypography variant="body2" color="text">
                        <strong>Lower fire risk</strong> → higher threshold → fewer false alarms
                      </MDTypography>
                    </li>
                  </MDBox>
                </MDBox>

                {/* Paragraph 3: Why this matters */}
                <MDTypography variant="body2" color="text" mb={2}>
                  In high-risk conditions, fires are more likely and spread faster, so early detection 
                  is critical. In lower-risk conditions, the system becomes more conservative to avoid 
                  unnecessary false alarms.
                </MDTypography>

                {/* Highlight Box */}
                <MDBox 
                  p={2} 
                  sx={{ 
                    backgroundColor: '#e3f2fd', 
                    borderRadius: '8px',
                    borderLeft: '4px solid #2196f3'
                  }}
                >
                  <MDTypography variant="body2" color="text" fontStyle="italic">
                    <strong>Bottom Line:</strong> This approach balances speed vs. accuracy, shifting 
                    the system toward early detection when it matters most.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default AdaptiveThresholdSection;
